import AgendaItem from "@/domain/entities/agenda/AgendaItem";
import AgendaItemAttendee from "@/domain/entities/agenda/AgendaItemAttendee";
import AgendaItemResponseStatus, {
    ResponseStatus,
} from "@/domain/entities/agenda/AgendaItemResponseStatus";
import EventDetail from "@/domain/entities/emails/EventDetail";
import { useToast } from "@/hooks/utils/useToast";
import { queryKeys } from "@/lib/queryKeys";
import { setOpenedEvent } from "@/stores/eventStore";
import mailStore from "@/stores/mailStore";
import { useAgenda } from "@cs_teamwaydev/mailvista-sdk_react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { shallow, useSelector } from "@tanstack/react-store";
import { useTranslation } from "react-i18next";
import useGetUserProfile from "../useGetUserProfile";
import useErrorToast from "../utils/useErrorToast";

const useManageInvitationToEvent = (
    setUpdatedEvent: (event: AgendaItem | EventDetail) => void,
    eventToUpdate: AgendaItem | EventDetail,
) => {
    const agenda = useAgenda();
    const showErrorToast = useErrorToast();
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { t } = useTranslation();
    const openedMail = useSelector(mailStore, (state) => state.openedMail, {
        compare: shallow,
    });
    const { data: user } = useGetUserProfile();

    const acceptInvitation = useMutation({
        mutationFn: async () => {
            if (!eventToUpdate.id) {
                throw new Error("Event ID is required");
            }

            await agenda.acceptAgendaItem(eventToUpdate.id);

            // We update the event with the new status for immediate user feedback
            // and for Vistassistant to be able to display the correct status
            if (eventToUpdate instanceof AgendaItem) {
                const userEmail = user?.profile.email.toLowerCase();
                setUpdatedEvent(
                    new AgendaItem({
                        ...eventToUpdate,
                        attendees: eventToUpdate.attendees.map((attendee) => {
                            if (
                                attendee.emailAddress.address.toLowerCase() !==
                                userEmail
                            ) {
                                return attendee;
                            }

                            return new AgendaItemAttendee({
                                ...attendee,
                                status: new AgendaItemResponseStatus({
                                    ...attendee.status,
                                    time: new Date().toISOString(),
                                    response: ResponseStatus.Accepted,
                                }),
                            });
                        }),
                    }),
                );
            }
            if (eventToUpdate instanceof EventDetail) {
                setUpdatedEvent(
                    new EventDetail({
                        ...eventToUpdate,
                        userResponseStatus: ResponseStatus.Accepted,
                    }),
                );
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.agendaEvents.all(),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.mailThread.detail(
                    openedMail?.internetMessageId,
                ),
            });
            toast({
                title: t("email.acceptInvitation.success"),
                variant: "success",
            });
        },
        onError: (error) => {
            console.error(error);
            showErrorToast({
                titleTranslationKey: "email.acceptInvitation.error",
            });
        },
    });

    const rejectInvitation = useMutation({
        mutationFn: async () => {
            if (!eventToUpdate.id) {
                throw new Error("Event ID is required");
            }

            await agenda.declineAgendaItem(eventToUpdate.id);

            // We update the event with the new status for immediate user feedback
            // and for Vistassistant to be able to display the correct status
            if (eventToUpdate instanceof AgendaItem) {
                const userEmail = user?.profile.email.toLowerCase();
                setUpdatedEvent(
                    new AgendaItem({
                        ...eventToUpdate,
                        attendees: eventToUpdate.attendees.map((attendee) => {
                            if (
                                attendee.emailAddress.address.toLowerCase() !==
                                userEmail
                            ) {
                                return attendee;
                            }

                            return new AgendaItemAttendee({
                                ...attendee,
                                status: new AgendaItemResponseStatus({
                                    ...attendee.status,
                                    time: new Date().toISOString(),
                                    response: ResponseStatus.Declined,
                                }),
                            });
                        }),
                    }),
                );
            }
            if (eventToUpdate instanceof EventDetail) {
                setUpdatedEvent(
                    new EventDetail({
                        ...eventToUpdate,
                        userResponseStatus: ResponseStatus.Declined,
                    }),
                );
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.agendaEvents.all(),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.mailThread.detail(
                    openedMail?.internetMessageId,
                ),
            });
            setOpenedEvent(undefined);
            toast({
                title: t("email.rejectInvitation.success"),
                variant: "success",
            });
        },
        onError: (error) => {
            console.error(error);
            showErrorToast({
                titleTranslationKey: "email.rejectInvitation.error",
            });
        },
    });

    const maybeInvitation = useMutation({
        mutationFn: async () => {
            if (!eventToUpdate.id) {
                throw new Error("Event ID is required");
            }

            await agenda.tentativelyAcceptAgendaItem(eventToUpdate.id);

            // We update the event with the new status for immediate user feedback
            // and for Vistassistant to be able to display the correct status
            if (eventToUpdate instanceof AgendaItem) {
                const userEmail = user?.profile.email.toLowerCase();
                setUpdatedEvent(
                    new AgendaItem({
                        ...eventToUpdate,
                        attendees: eventToUpdate.attendees.map((attendee) => {
                            if (
                                attendee.emailAddress.address.toLowerCase() !==
                                userEmail
                            ) {
                                return attendee;
                            }

                            return new AgendaItemAttendee({
                                ...attendee,
                                status: new AgendaItemResponseStatus({
                                    ...attendee.status,
                                    time: new Date().toISOString(),
                                    response:
                                        ResponseStatus.TentativelyAccepted,
                                }),
                            });
                        }),
                    }),
                );
            }
            if (eventToUpdate instanceof EventDetail) {
                setUpdatedEvent(
                    new EventDetail({
                        ...eventToUpdate,
                        userResponseStatus: ResponseStatus.TentativelyAccepted,
                    }),
                );
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.agendaEvents.all(),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.mailThread.detail(
                    openedMail?.internetMessageId,
                ),
            });
            toast({
                title: t("email.maybeInvitation.success"),
                variant: "success",
            });
        },
        onError: (error) => {
            console.error(error);
            showErrorToast({
                titleTranslationKey: "email.maybeInvitation.error",
            });
        },
    });

    return {
        acceptInvitation,
        rejectInvitation,
        maybeInvitation,
    };
};

export default useManageInvitationToEvent;
